import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { ApiError, api } from '../api';
import { useI18n } from '../i18n';

/** A company as the Lithuanian registers know it (see apps/api/src/routes/registry.ts). */
export interface RegistryCompany { code: string; name: string; vatCode: string | null; address: string | null; deregistered: boolean }

type Status = 'idle' | 'loading' | 'found' | 'noAddress' | 'deregistered' | 'notFound' | 'error';

/**
 * Filling a party from the registers: a button next to the company code, and name suggestions while
 * typing (picking one fills the rest). `apply` receives the company; empty registry fields are left alone.
 */
export function useRegistry(apply: (company: RegistryCompany) => void) {
  const { t } = useI18n();
  const listId = useId();
  const [status, setStatus] = useState<Status>('idle');
  const [suggestions, setSuggestions] = useState<{ code: string; name: string }[]>([]);
  const [typed, setTyped] = useState('');
  const applyRef = useRef(apply);
  applyRef.current = apply;

  const lookup = async (raw: string) => {
    const code = raw.replace(/\s/g, '');
    if (!/^\d{7,9}$/.test(code)) { setStatus('notFound'); return; }
    setStatus('loading');
    try {
      const { company } = await api.get<{ company: RegistryCompany }>(`api/v1/registry/company?code=${code}`);
      applyRef.current(company);
      setStatus(company.deregistered ? 'deregistered' : company.address ? 'found' : 'noAddress');
    } catch (error) {
      setStatus(error instanceof ApiError && error.code === 'registry_not_found' ? 'notFound' : 'error');
    }
  };

  useEffect(() => {
    const q = typed.trim();
    if (q.length < 3 || /^\d+$/.test(q)) { setSuggestions([]); return; }
    const timer = window.setTimeout(() => {
      api.get<{ companies: { code: string; name: string }[] }>(`api/v1/registry/search?q=${encodeURIComponent(q)}`)
        .then((r) => setSuggestions(r.companies)).catch(() => setSuggestions([]));
    }, 400);
    return () => window.clearTimeout(timer);
  }, [typed]);

  /** Spread onto the name input: suggestions appear as the user types; choosing one fills the form. */
  const nameInput = {
    list: listId,
    autoComplete: 'off',
    onInput: (event: FormEvent<HTMLInputElement>) => {
      const value = event.currentTarget.value;
      const hit = suggestions.find((s) => s.name === value);
      if (hit) { setSuggestions([]); void lookup(hit.code); } else setTyped(value);
    },
  };

  const message: Record<Status, string | null> = {
    idle: null, loading: t('Ieškoma registre…'), found: t('Užpildyta iš registro. Patikrinkite adresą.'), noAddress: t('Užpildyta iš registro. Buveinės adreso vieši duomenys neskelbia — įveskite ranka.'),
    deregistered: t('Dėmesio: įmonė išregistruota.'), notFound: t('Registre tokio įmonės kodo nėra.'), error: t('Registras šiuo metu nepasiekiamas. Įveskite duomenis ranka.'),
  };

  const extras = (
    <>
      <datalist id={listId}>{suggestions.map((s) => <option key={s.code} value={s.name}>{s.code}</option>)}</datalist>
      {message[status] ? <small className={`registry-note registry-note--${status}`} role="status">{message[status]}</small> : null}
    </>
  );

  const button = (code: () => string) => (
    <button type="button" className="registry-button" disabled={status === 'loading'} onClick={() => void lookup(code())} title={t('Užpildyti pagal įmonės kodą iš Juridinių asmenų ir PVM mokėtojų registrų')}>
      {t('Iš registro')}
    </button>
  );

  return { nameInput, button, extras, lookup };
}

/** For forms with uncontrolled inputs: writes registry values into the named fields. */
export function fillFields(form: HTMLFormElement | null, fields: { name?: string; code?: string; vatCode?: string; address?: string }, company: RegistryCompany): void {
  if (!form) return;
  const set = (field: string | undefined, value: string | null) => {
    const input = field ? form.elements.namedItem(field) : null;
    if (value && (input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement)) input.value = value;
  };
  set(fields.name, company.name);
  set(fields.code, company.code);
  set(fields.vatCode, company.vatCode);
  set(fields.address, company.address);
}
